import * as React from "react";
import {Link} from "react-router-dom";
import {IAssociatedPlan} from "../../model/ClientsAndPlans.model";
import {COUNTRY_SELECT_VALUES} from "./AdminAllClients";
import {LANGUAGE_SELECT_VALUES} from "./AdminEditPlan.component";
import {ISelectValue} from "../common/Select.component";
import {FormattedMessage} from "react-intl";
import {getTranslation} from "../../utils/translationUtil";

export interface IAssociatedPlansTableProps {
    plans: IAssociatedPlan[];
}

export const AssociatedPlansTable: React.FunctionComponent<IAssociatedPlansTableProps> = (props: IAssociatedPlansTableProps) => {
    const getCountryName = (countryId: number): string => {
        const country = COUNTRY_SELECT_VALUES.find(c => c.id === countryId);
        return country ? country.name : "";
    };

    const getLanguageName = (languageId?: number): string => {
        if (!languageId) return "";
        const language = LANGUAGE_SELECT_VALUES.find((l: ISelectValue) => l.id === languageId);
        return language ? language.name : "";
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            (event.target as HTMLAnchorElement).click();
        }
    };

    const renderPlanRow = (plan: IAssociatedPlan) => {
        return (
            <tr className="associated-plans__row" key={plan.id} data-testid={`associated-plan-row-${plan.id}`}>
                <td className="associated-plans__plan-name">
                    <Link
                        to={`/admin/plan/${plan.id}`}
                        className="associated-plans__plan-link"
                        onKeyDown={handleKeyDown}
                        data-testid={`associated-plan-link-${plan.id}`}
                    >
                        {plan.name}
                    </Link>
                </td>
                <td className="associated-plans__country">{getCountryName(plan.countryId)}</td>
                <td className="associated-plans__language">{getLanguageName(plan.languageId)}</td>
                <td className="associated-plans__users">
                    <span className="user-count">
                        <i className="fa-solid fa-users" aria-hidden="true" /> {plan.userCount ?? 0}
                    </span>
                </td>
            </tr>
        );
    };

    const renderEmptyState = () => {
        return (
            <tr>
                <td colSpan={4} className="associated-plans__empty-state">
                    <FormattedMessage id="admin.no-plans-associated" defaultMessage="No plans associated" />
                </td>
            </tr>
        );
    };

    return (
        <div className="associated-plans__container" data-testid="associated-plans-container">
            <div className="associated-plans__header">
                <h3 className="associated-plans__title">
                    <FormattedMessage id="admin.associated-plans" defaultMessage="Associated Plans" />
                </h3>
                <span 
                    className="associated-plans__count-badge" 
                    aria-label={getTranslation("admin.plan-count", `${props.plans.length} plans`)}
                    data-testid="associated-plans-count"
                >
                    {props.plans.length}
                </span>
            </div>
            <table className="associated-plans__table" role="grid" aria-label={getTranslation("admin.associated-plans-table", "Associated plans table")}>
                <thead>
                    <tr>
                        <th scope="col">
                            <FormattedMessage id="admin.plan-name" defaultMessage="Plan Name" />
                        </th>
                        <th scope="col">
                            <FormattedMessage id="admin.country" defaultMessage="Country" />
                        </th>
                        <th scope="col">
                            <FormattedMessage id="admin.language" defaultMessage="Language" />
                        </th>
                        <th scope="col">
                            <FormattedMessage id="admin.users" defaultMessage="Users" />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {props.plans.length > 0 ? props.plans.map(renderPlanRow) : renderEmptyState()}
                </tbody>
            </table>
        </div>
    );
};
